import { useState } from "react";
import { toast } from "sonner";
import { Loader2, GitCompare } from "lucide-react";
import { Button } from "../components/ui/button";
import { MarkdownCell, CodeCell, Stream } from "../components/Cell";
import { SpeciesSearchBox } from "../components/SpeciesSearchBox";
import { CountrySelect } from "../components/CountrySelect";
import { lookupSpecies, compareSpecies, errMsg } from "../lib/api";
import { countryName } from "../lib/countries";
import { GbifMap } from "../components/GbifMap";

const Col = ({ d, side, country }) => {
  const g = d.gbif || {}, i = d.inaturalist || {};
  const key = g.acceptedUsageKey || g.usageKey;
  const photos = [...(i.taxon_photos || []).slice(0, 3), ...(g.media || []).slice(0, 3)];
  return (
    <div data-testid={`compare-col-${side}`} className="min-w-0 space-y-3">
      <div>
        <div className="font-mono text-[11px] text-muted-foreground">{side.toUpperCase()}</div>
        <div className="text-xl font-semibold italic">{i.name || g.canonicalName || d.query}</div>
        <div className="text-sm text-muted-foreground">{i.common_name || "—"} · {g.hierarchy?.find((h) => h.rank === "family")?.name || ""}</div>
      </div>
      <Stream>{`gbif_key      : ${key ?? "—"} (${g.matchType})\noccurrences   : ${(g.occurrences?.count ?? 0).toLocaleString()}${country ? ` in ${country}` : ""}\ninat_obs      : ${(i.observations_count ?? 0).toLocaleString()} (worldwide)${country && i.observations_in_region != null ? `\ninat_in_region: ${i.observations_in_region.toLocaleString()}` : ""}`}</Stream>
      <div data-testid={`compare-photos-${side}`} className="grid grid-cols-3 gap-1.5">
        {photos.map((p, k) => <img key={k} src={p.photo} alt="" loading="lazy" className="aspect-square w-full object-cover rounded border" />)}
      </div>
      {key && (
        <GbifMap testid={`compare-map-${side}`} taxonKey={key} country={country} bbox={d.region?.bbox} style={side === "a" ? "classic.poly" : "purpleYellow.poly"} />
      )}
    </div>
  );
};

const Picker = ({ label, value, onPick }) => (
  <div className="space-y-1">
    <div className="text-xs font-mono text-muted-foreground">{label} = {value ? `"${value}"` : "None"}</div>
    <SpeciesSearchBox onSearch={onPick} initial={value || ""} />
  </div>
);

export default function ComparePage({ setKernel }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [country, setCountry] = useState(null);
  const [res, setRes] = useState(null);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true); setRes(null); setAi(null); setKernel("busy");
    try {
      const [da, db] = await Promise.all([lookupSpecies(a, country, countryName(country)), lookupSpecies(b, country, countryName(country))]);
      setRes({ a: da, b: db, country });
      setAi(await compareSpecies(da.inaturalist?.name || da.query, db.inaturalist?.name || db.query));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setKernel("idle"); }
  };

  return (
    <div>
      <MarkdownCell><h1>Compare species</h1><p>Pick two similar moths to see their photos, GBIF occurrence maps and key distinguishing features side by side.</p></MarkdownCell>
      <div className="relative z-30"><CodeCell n={1} testid="compare-input-cell" code={`a, b = ${a ? `"${a}"` : "None"}, ${b ? `"${b}"` : "None"}\ncompare(a, b, country=${country ? `"${country}"` : "None"})`}>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="relative z-20"><Picker label="a" value={a} onPick={setA} /></div>
          <Picker label="b" value={b} onPick={setB} />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <CountrySelect value={country} onChange={setCountry} testid="compare-country-select" />
          <Button data-testid="compare-run-button" size="sm" disabled={!a || !b || busy} onClick={run}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitCompare className="h-4 w-4 mr-1.5" />}Compare
          </Button>
          <span className="text-xs font-mono text-muted-foreground">tip: pick a name from the suggestions or press Enter</span>
        </div>
      </CodeCell></div>
      {res && (
        <CodeCell n={2} testid="compare-side-cell" code="side_by_side(gbif_a, inat_a, gbif_b, inat_b)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6"><Col d={res.a} side="a" country={res.country} /><Col d={res.b} side="b" country={res.country} /></div>
        </CodeCell>
      )}
      {(ai || (res && busy)) && (
        <CodeCell n={3} testid="compare-features-cell" running={busy} code={'features = model.compare_features(a, b)   # AI-generated, verify with literature\nfeatures'}>
          {ai && (
            <div className="space-y-3">
              <div className="overflow-x-auto"><table data-testid="compare-features-table" className="text-[13px] w-full min-w-[560px]">
                <thead><tr className="border-b font-semibold text-left"><th className="px-2 py-1">feature</th><th className="px-2 py-1 italic">{ai.a}</th><th className="px-2 py-1 italic">{ai.b}</th></tr></thead>
                <tbody>{(ai.features || []).map((f) => <tr key={f.feature} className="odd:bg-secondary/70 align-top"><td className="px-2 py-1 font-mono text-xs font-semibold whitespace-nowrap">{f.feature}</td><td className="px-2 py-1">{f.a}</td><td className="px-2 py-1">{f.b}</td></tr>)}</tbody>
              </table></div>
              <Stream testid="compare-tell-apart">{`confusion_risk : ${ai.confusion_risk}\nhow to tell apart:\n${(ai.how_to_tell_apart || []).map((x) => `  • ${x}`).join("\n")}`}</Stream>
            </div>
          )}
        </CodeCell>
      )}
    </div>
  );
}
